/**
 * Clinic header block — logo + contact details.
 * Exports both a react-pdf version (for PDF generation) and an HTML version (for screen preview).
 */
import { Image, StyleSheet, Text, View } from '@react-pdf/renderer'

import snootsLogo from '@/frontend/assets/snoots-logo.png'
import { mm } from '@/frontend/features/printing/pdf-utils'

export interface ClinicHeaderProps {
  clinic: {
    name?: string
    address: string
    phone: string
    email: string
    website: string
  }
  /** Show the clinic name as a heading above the contact details. Defaults to false. */
  showName?: boolean
}

// ---------------------------------------------------------------------------
// Preview (HTML)
// ---------------------------------------------------------------------------

export function ClinicHeaderPreview({ clinic, showName = false }: ClinicHeaderProps) {
  return (
    <div className="mb-6 flex items-center gap-4">
      <img src={snootsLogo} alt="Snoots" className="w-40" />
      <div className="text-sm text-muted-foreground leading-relaxed">
        {showName && clinic.name && <p className="font-semibold text-foreground">{clinic.name}</p>}
        <p>Address: {clinic.address}</p>
        <p>Phone: {clinic.phone}</p>
        <p>Email: {clinic.email}</p>
        <p>Website: {clinic.website}</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// PDF (react-pdf)
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: mm(10),
  },
  logo: {
    width: mm(55),
    marginRight: mm(10),
  },
  details: {
    fontSize: 9,
    color: '#333',
    lineHeight: 1.5,
  },
  name: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    color: '#000',
    marginBottom: mm(1),
  },
})

export function ClinicHeader({ clinic, showName = false }: ClinicHeaderProps) {
  return (
    <View style={styles.row}>
      <Image src={snootsLogo} style={styles.logo} />
      <View style={styles.details}>
        {showName && clinic.name && <Text style={styles.name}>{clinic.name}</Text>}
        <Text>Address: {clinic.address}</Text>
        <Text>Phone: {clinic.phone}</Text>
        <Text>Email: {clinic.email}</Text>
        <Text>Website: {clinic.website}</Text>
      </View>
    </View>
  )
}
